import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { AlertContext } from 'AppContext'

import {
  Button,
  ButtonIcon,
  ButtonText,
  Checkbox,
  CheckboxGroup,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  FormControl,
  FormControlError,
  FormControlErrorText,
  FormControlErrorIcon,
  FormControlLabel,
  FormControlLabelText,
  Heading,
  Input,
  HStack,
  VStack,
  Text,
  InputField,
  CheckIcon
} from '@gluestack-ui/themed'

import { Select } from 'components/Select'
import { generateCapabilitiesString } from 'api/Wifi'

let modes = [
  { label: '5 & 6 GHz', value: 'a' },
  { label: '2.4 GHz', value: 'g' }
]

let convertChannelToFrequency = (band, channel) => {
  let frequency;

  if (band === "2.4") {
    frequency = 2412 + (channel - 1) * 5;
    if (channel === 14) {
      frequency = 2484;
    }
  } else if (band === "5") {
    if (channel >= 1 && channel <= 144) {
      frequency = 5000 + channel * 5;
    } else if (channel >= 149 && channel <= 169) {
      frequency = 5000 + (channel - 1) * 5;
    } else if (channel >= 184 && channel <= 196) {
      frequency = 4000 + channel * 5;
    }
  } else if (band === "6") {
    if (channel >= 1 && channel <= 253) {
      frequency = 5940 + channel * 5;
    }
  }

  return frequency
}

const WifiChannelParameters = ({
  iface,
  setIface,
  config,
  iws,
  regs,
  curInterface,
  onSubmit,
  ...props
}) => {
  const context = useContext(AlertContext)
  const [channel, setChannel] = useState(0)
  const [bandwidth, setBandwidth] = useState(0)
  const [bandwidthLabel, setBandwidthLabel] = useState('')
  const [mode, setMode] = useState(config.hw_mode) //('a')
  const [modeLabel, setModeLabel] = useState('5/6 GHz')
  const [errors, setErrors] = useState({})
  const [disable160, setDisable160] = useState(true)
  const [disable320, setDisable320] = useState(true)
  const [disableWifi6, setDisableWifi6] = useState(true)
  const [disableWifi7, setDisableWifi7] = useState(true)
  const [disableDFS, setDisableDFS] = useState(true)
  const [groupValues, setGroupValues] = useState([])

  // MLO Link 1 state
  const [mloChannel, setMloChannel] = useState(0)
  const [mloBandwidth, setMloBandwidth] = useState(40)
  const [mloBandwidthLabel, setMloBandwidthLabel] = useState('')

  //some wifi devices were reported to crash during auth if GCMP was enabled,
  // disable this less common cipher by default but let people enable it
  const [disableGCMP, setDisableGCMP] = useState(false)
  const [selectedMode, setSelectedMode] = useState(modes[0])

  let bandwidth5 = [
    { label: '20 MHz', value: 20 },
    { label: '40 MHz', value: 40 },
    { label: '80 MHz', value: 80 },
    { label: '160 MHz', disabled: disable160, value: 160 },
    { label: '320 MHz (WiFi 7)', disabled: disable320, value: 320 },
    { label: '80+80 MHz', disabled: true, value: 8080 }
  ]

  const bandwidth24 = [
    { label: '20 MHz', value: 20 },
    { label: '40 MHz', value: 40 }
  ]

  const [bandwidths, setBandwidths] = useState(bandwidth5)

  const handleModeChange = (newMode) => {
    let x = modes.find((v) => v.value == newMode)?.label
    setModeLabel(x)
  }

  useEffect(() => {
    //work around for selectedLabel not working
    //instead we use the labels as the value
    //but then we hook updating the mode.
    let x = modes.find((v) => v.label == modeLabel)?.value
    setMode(x)
    //update bandwidth
    if (x == 'a') {
      setBandwidth(80)
      handleBandwidthChange(bandwidth5, 80)
    } else {
      setBandwidth(40)
      handleBandwidthChange(bandwidth24, 40)
    }
  }, [modeLabel])

  //workarounds for selectedLabel again
  const handleBandwidthChange = (bands, newBandwidth) => {
    let x = bands.find((v) => v.value == newBandwidth)?.label
    setBandwidths(bands)
    setBandwidthLabel(x)
  }

  useEffect(() => {
    let x = mode == 'a' ? bandwidth5 : bandwidth24
    setBandwidths(x)
  }, [mode])

  useEffect(() => {
    let x = bandwidths.find((v) => v.label == bandwidthLabel)?.value
    setBandwidth(x)
  }, [bandwidthLabel])

  useEffect(() => {
    //props.config.interface should match TBD

    // switch to config-based settings
    setMode(config.hw_mode)
    handleModeChange(config.hw_mode)
    setSelectedMode(modes.find((v) => v.value == config.hw_mode))
    if (config.op_class > 130 && config.channel == 0) {
      //select the 6-e acs in this case
      setChannel("6GHz")
    } else {
      setChannel(config.channel)
    }

    let newBandwidth = 40
    if (config.vht_oper_chwidth == 0) {
      newBandwidth = 40
    } else if (config.vht_oper_chwidth == 1) {
      newBandwidth = 80
    } else if (config.vht_oper_chwidth == 2) {
      newBandwidth = 160
    } else if (config.vht_oper_chwidth == 3) {
      setBandwidth(8080)
      newBandwidth = 8080
    } else {
      // no vht. fall through
      if (config.ht_capab && config.ht_capab.includes('HT40')) {
        newBandwidth = 40
      } else {
        newBandwidth = 20
      }
    }

    setBandwidth(newBandwidth)
    handleBandwidthChange(
      config.hw_mode == 'a' ? bandwidth5 : bandwidth24,
      newBandwidth
    )


    let hasGCMP = config.rsn_pairwise.includes('GCMP')
    if (hasGCMP) {
      setGroupValues(groupValues.concat('gcmpon'))
    }

    if (config.ieee80211ax == 1) {
      if (!groupValues.includes('wifi6')) {
        setGroupValues(groupValues.concat('wifi6'))
      }
    }

    if (config.ieee80211be == 1) {
      if (!groupValues.includes('wifi7')) {
        setGroupValues((prev) => prev.includes('wifi7') ? prev : [...prev, 'wifi7'])
      }
    }

    if (config.mld_ap == 1) {
      if (!groupValues.includes('mlo')) {
        setGroupValues((prev) => prev.includes('mlo') ? prev : [...prev, 'mlo'])
      }
    }

    // Load MLO link config
    if (config.mlo_channel) {
      setMloChannel(config.mlo_channel)
    }
    if (config.mlo_bandwidth) {
      setMloBandwidth(config.mlo_bandwidth)
    }

    //set bw and channels
    for (let iw of iws) {
      if (iw.devices[iface]) {
        let cur_device = iw.devices[iface]
        if (!cur_device) continue

        //check for radar/DFS support
        // Check extended features first (RADAR_BACKGROUND or DFS_OFFLOAD)
        if (iw.supported_extended_features) {
          for (let feature of iw.supported_extended_features) {
            if (feature.includes('RADAR_BACKGROUND') || feature.includes('DFS_OFFLOAD')) {
              setDisableDFS(false)
              break
            }
          }
        }

        // Also check if any channels have radar detection capability
        // — the driver supports DFS if it lists radar channels
        for (let band of iw.bands) {
          if (band.frequencies) {
            for (let freq of band.frequencies) {
              if (freq.includes('radar detection')) {
                setDisableDFS(false)
                break
              }
            }
          }
        }

        //enable 160 MHz if supported by the card
        for (let band of iw.bands) {
          if (band.vht_capabilities) {
            for (let capability of band.vht_capabilities) {
              if (
                capability.includes('160 MHz') ||
                capability.includes('160Mhz')
              ) {
                //does card support 160, but doesnt account for regulatory
                //we do that under channel lists later.
                setDisable160(false)
              }
            }
          }

          if (band.he_phy_capabilities) {
            setDisableWifi6(false)
          }

          if (band.eht_phy_capabilities) {
            setDisableWifi7(false)
            //6 GHz bands support 320 MHz with WiFi 7
            if (band.band && band.band.includes('Band 4')) {
              setDisable320(false)
            }
          }
        }

      }
    }
  }, [iface, config, iws, curInterface])

  const checkRegsDisable = (frequency, bandwidth) => {
    if (!regs || !regs.bands) return false
    for (let reg_band of regs.bands) {
      if (frequency >= reg_band.start && frequency < reg_band.end) {
        //too much bandwidth asked for, ex reg says 80, but asking for 160
        if (bandwidth > reg_band.max_bandwidth) {
          return true
        }
        //160 runs past end of band
        //we subract 10 because the center frequency is what is described
        if (frequency >= reg_band.start && (frequency + bandwidth - 10 > reg_band.end)) {
          return true
        }
        break
      }
    }

    return false
  }

  const enumerateChannelOptions = () => {
    //const iface = props.config.interface
    let validChannels = []
    let saw_6e = false

    let expectedFreq = mode == 'a' ? '5' : '2'
    for (let iw of iws) {
      if (!iw.devices[iface]) {
        continue
      }

      for (let band of iw.bands) {
        //if the band does not match the current mode, skip it
        if (!band.frequencies || band.frequencies[0][0] != expectedFreq) {
          continue
        }


        for (let freq of band.frequencies) {
          let frequency = parseInt(freq.split(' ')[0])

          let channelNumber = parseInt(freq.split(' ')[2].slice(1, -1))
          let channelLabel = channelNumber


          let isDisabled = false
          if (freq.includes('disabled')) {
            isDisabled = true
            channelLabel += ' disabled'
          } else if (freq.includes('no IR')) {
            isDisabled = true
            channelLabel += ' No Initial Radiation (No-IR)'
          } else if (freq.includes('radar')) {
            channelLabel += ' DFS'
            // Disable DFS channels if:
            // 1. IEEE 802.11h is not enabled, OR
            // 2. Hardware doesn't support radar background detection
            if (config.ieee80211h !== 1 || disableDFS) {
              isDisabled = true
              if (disableDFS && config.ieee80211h === 1) {
                channelLabel += ' (no radar support)'
              }
            }
          }

          //bandwith check, do not list start indices that
          // are nonsense for 5/6ghz.
          if (mode == 'a') {
            if (bandwidth == 320) {
              //320 MHz only valid for 6 GHz
              if (frequency < 5900) {
                continue
              }
              //6ghz 320 MHz valid start channels
              if (frequency % 320 != 195 && frequency % 320 != 35) {
                continue
              }
            } else if (bandwidth == 160) {
              //5 ghz and 6ghz offsets
              if (frequency % 160 != 60 && frequency % 160 != 35) {
                continue
              }
            } else if (bandwidth == 80) {
              //5 ghz and 6ghz offsets
              if (frequency % 80 != 60 && frequency % 80 != 35 && frequency % 80 != 65) {
                continue
              }
            } else if (bandwidth == 40) {
              //5 ghz and 6ghz offsets
              if (frequency % 40 != 20 && frequency % 40 != 35) {
                continue
              }
            }
          }

          if (isDisabled == false) {

            if (frequency > 5900) {
              saw_6e = true
            }

            //if not disabled yet, check the regs db also for validity
            //6095 MHz [29] (12.0 dBm) (no IR)
            isDisabled = checkRegsDisable(frequency, bandwidth)
          }

          validChannels.push({
            value: channelNumber.toString(),
            label: channelLabel,
            toolTip: freq,
            disabled: isDisabled
          })
        }
      }
    }

    validChannels.push({
      value: "0",
      label: "Automatic Channel Selection",
      toolTip: "Automatic Channel Selection",
      disabled: false
    })

    if (saw_6e) {
      validChannels.push({
        value: "6GHz",
        label: "[6GHz] Automatic Channel Selection",
        toolTip: "[6GHz] Automatic Channel Selection",
        disabled: false
      })
    }

    //move enabled to top
    validChannels.sort((a, b) => {
      if (a.disabled && !b.disabled) {
        return 1
      } else if (!a.disabled && b.disabled) {
        return -1
      } else {
        return 0
      }
    });

    return validChannels
  }

  const isValid = () => {
    if (!bandwidth) {
      setErrors({ bandwidth: true })
      return false
    }

    if (channel != 0 && !channel) {
      setErrors({ channel: true })
      return false
    }

    return true
  }

  const getLLAIfaceAddr = (iface) => {
    for (let iw of iws) {
      if (iw.devices[iface]) {
        let base = iw.devices[iface].addr
        let updatedByte = parseInt(base.substr(0, 2), 16) | 2
        const updatedByteHex = updatedByte
          .toString(16)
          .padStart(2, '0')
          .toUpperCase()
        return updatedByteHex + base.substr(2)
      }
    }
    return null
  }

  const handleSubmit = () => {
    if (!isValid()) {
      return
    }


    let ehtEnabled = groupValues.includes('wifi7')

    let wifiParameters = {
      //Interface: iface,
      Channel: channel,
      Mode: mode,
      Bandwidth: bandwidth,
      HT_Enable: true,
      VHT_Enable: mode == 'a' ? true : false,
      HE_Enable: true,
      EHT_Enable: ehtEnabled
    }

    if (groupValues.includes('gcmpon')) {
      wifiParameters.Rsn_pairwise = "CCMP CCMP-256 GCMP GCMP-256"
    } else {
      wifiParameters.Rsn_pairwise = "CCMP CCMP-256"
    }

    if (groupValues.includes('wifi6')) {
      wifiParameters.He_mu_beamformer = 1
      wifiParameters.He_su_beamformee = 1
      wifiParameters.He_su_beamformer = 1
      wifiParameters.Ieee80211ax = 1
    } else {
      wifiParameters.He_mu_beamformer = 0
      wifiParameters.He_su_beamformee = 0
      wifiParameters.He_su_beamformer = 0
      wifiParameters.Ieee80211ax = 0
    }

    if (ehtEnabled) {
      wifiParameters.Ieee80211be = 1
    } else {
      wifiParameters.Ieee80211be = 0
    }

    if (groupValues.includes('mlo')) {
      wifiParameters.Mld_ap = 1
      wifiParameters.Mlo_channel = parseInt(mloChannel)
      wifiParameters.Mlo_bandwidth = parseInt(mloBandwidth)

      // Determine hw_mode + band index for the MLO link from channel frequency
      let mloMode = 'a'
      let mloBandIndex = null
      for (let iw of iws) {
        if (!iw.devices[iface]) continue
        for (let band of iw.bands) {
          if (!band.frequencies) continue
          for (let freq of band.frequencies) {
            let chNum = parseInt(freq.split(' ')[2].slice(1, -1))
            if (chNum === parseInt(mloChannel)) {
              let f = parseInt(freq.split(' ')[0])
              if (f < 3000) {
                mloMode = 'g'
                mloBandIndex = 1
              } else if (f < 5950) {
                mloBandIndex = 2
              } else {
                mloBandIndex = 4
              }
            }
          }
        }
      }
      wifiParameters.Mlo_hw_mode = mloMode

      // Derive ht_capab/vht_capab from the MLO link's band (not primary's)
      if (mloBandIndex !== null) {
        let phy = iws.find((iw) => iw.devices && iw.devices[iface])
        if (phy) {
          let [htArr, vhtArr] = generateCapabilitiesString(
            { [iface]: phy },
            iface,
            mloBandIndex
          )
          if (htArr && htArr.length) {
            htArr.sort()
            wifiParameters.Mlo_ht_capab = htArr.join('')
          }
          if (vhtArr && vhtArr.length) {
            vhtArr.sort()
            wifiParameters.Mlo_vht_capab = vhtArr.join('')
          }
        }
      }
    } else {
      wifiParameters.Mld_ap = 0
    }

    onSubmit(wifiParameters)
  }

  let checkboxProps = disableWifi6 ? { isDisabled: true } : {}
  let wifi7CheckboxProps = disableWifi7 ? { isDisabled: true } : {}
  let mloCheckboxProps = disableWifi7 ? { isDisabled: true } : {}
  let gcmpCheckboxProps = disableGCMP ? { isDisabled: false } : {}

  return (
    <>
      <HStack justifyContent="space-between" p="$4">
        <Heading size="sm">Channel Selection</Heading>
      </HStack>

      <VStack
        space="md"
        bg="$backgroundCardLight"
        sx={{ _dark: { bg: '$backgroundCardDark' } }}
        p="$4"
        pb="$8"
      >
        <Text pb="$4" color="$muted500" flexWrap="wrap">
          Use the Channel Selection to make sure the correct Frequency,
          Bandwidth &amp; Channel is set. This will update your HostAP config.
        </Text>

        {disableDFS && mode == 'a' ? (
          <Text pb="$4" color="$warning600" flexWrap="wrap" size="sm">
            ⚠️ DFS channels are disabled: This WiFi card does not support
            radar detection (RADAR_BACKGROUND or DFS_OFFLOAD feature). DFS
            channels require hardware support for detecting radar signals to
            comply with regulations.
          </Text>
        ) : null}

        {groupValues.includes('mlo') ? (
          <Heading size="xs">Link 1</Heading>
        ) : null}

        <VStack
          sx={{ '@md': { flexDirection: 'row', alignItems: 'center' } }}
          space="md"
        >
          <FormControl flex={1}>
            <FormControlLabel>
              <FormControlLabelText>Frequency Band</FormControlLabelText>
            </FormControlLabel>
            {/*
            <Select
              selectedValue={channel}
              onValueChange={(value) => setChannel(parseInt(value))}
            >
              <Select.Item label="" value={0} />
              {enumerateChannelOptions().map((item) => (
                <Select.Item
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  isDisabled={item.disabled}
                />
              ))}
              */}
            <Select
              selectedValue={modeLabel}
              defaultValue={modeLabel}
              onValueChange={(value) => {
                setModeLabel(value)
              }}
            >
              {modes.map((item) => (
                <Select.Item
                  key={item.label}
                  label={item.label}
                  value={item.label}
                  isDisabled={item.disabled}
                />
              ))}
            </Select>
          </FormControl>

          <FormControl flex={1} isInvalid={'bandwidth' in errors}>
            <FormControlLabel>
              <FormControlLabelText>Bandwidth</FormControlLabelText>
            </FormControlLabel>
            <Select
              selectedValue={bandwidthLabel}
              onValueChange={(value) => {
                setBandwidthLabel(value)
              }}
            >
              <Select.Item label="" value={0} />
              {bandwidths.map((item) => (
                <Select.Item
                  key={item.label}
                  label={item.label}
                  value={item.label}
                  isDisabled={item.disabled}
                />
              ))}
            </Select>
            {'bandwidth' in errors ? (
              <FormControlError>
                <FormControlErrorText>
                  Invalid Bandwidth
                </FormControlErrorText>
              </FormControlError>
            ) : null}
          </FormControl>

          <FormControl flex={1} isInvalid={'channel' in errors}>
            <FormControlLabel for="Channel">
              <FormControlLabelText>Channel {channel}</FormControlLabelText>
            </FormControlLabel>
            <Select
              selectedValue={channel}
              onValueChange={(value) => setChannel(value)}
            >
              {enumerateChannelOptions().map((item) => (
                <Select.Item
                  label={item.label}
                  value={item.value}
                  isDisabled={item.disabled}
                />
              ))}
            </Select>
            {'channel' in errors ? (
              <FormControlError>
                <FormControlErrorText>
                  Invalid Channel
                </FormControlErrorText>
              </FormControlError>
            ) : null}
          </FormControl>

        </VStack>

        <CheckboxGroup
          value={groupValues}
          accessibilityLabel="WiFi Settings"
          onChange={(values) => {
            // MLO requires WiFi 7 (hostapd: cannot enable mld_ap without ieee80211be)
            let next = [...values]
            const mloAdded = next.includes('mlo') && !groupValues.includes('mlo')
            const wifi7Removed = !next.includes('wifi7') && groupValues.includes('wifi7')
            if (mloAdded && !next.includes('wifi7')) next.push('wifi7')
            if (wifi7Removed) next = next.filter((v) => v !== 'mlo')
            setGroupValues(next)
          }}
        >
          <HStack space="md" flexWrap="wrap" rowGap="$2">
            <Checkbox {...checkboxProps} value={'wifi6'}>
              <CheckboxIndicator mr="$2">
                <CheckboxIcon />
              </CheckboxIndicator>
              <CheckboxLabel>Wifi 6 (AX)</CheckboxLabel>
            </Checkbox>

            <Checkbox {...gcmpCheckboxProps} value={'gcmpon'}>
              <CheckboxIndicator mr="$2">
                <CheckboxIcon />
              </CheckboxIndicator>
              <CheckboxLabel>Enable GCMP Encryption</CheckboxLabel>
            </Checkbox>

            <Checkbox {...wifi7CheckboxProps} value={'wifi7'}>
              <CheckboxIndicator mr="$2">
                <CheckboxIcon />
              </CheckboxIndicator>
              <CheckboxLabel>WiFi 7 (BE)</CheckboxLabel>
            </Checkbox>

            <Checkbox {...mloCheckboxProps} value={'mlo'}>
              <CheckboxIndicator mr="$2">
                <CheckboxIcon />
              </CheckboxIndicator>
              <CheckboxLabel>MLO</CheckboxLabel>
            </Checkbox>

          </HStack>
        </CheckboxGroup>

        {groupValues.includes('mlo') ? (
          <VStack space="md" mt="$4">
            <Heading size="xs">Link 2</Heading>
            <HStack
              sx={{ '@md': { flexDirection: 'row', alignItems: 'center' } }}
              space="md"
            >
              <FormControl flex={1}>
                <FormControlLabel>
                  <FormControlLabelText>Bandwidth</FormControlLabelText>
                </FormControlLabel>
                <Select
                  selectedValue={mloBandwidthLabel || `${mloBandwidth} MHz`}
                  onValueChange={(value) => {
                    setMloBandwidthLabel(value)
                    let bw = parseInt(value)
                    if (!isNaN(bw)) setMloBandwidth(bw)
                  }}
                >
                  {(() => {
                    // Determine what band Link 2 is on
                    let getBand = (f) => f < 3000 ? 1 : f < 5900 ? 2 : 3

                    // First pass: find primary band
                    let primaryBand = 0
                    for (let iw of iws) {
                      if (!iw.devices[iface]) continue
                      for (let band of iw.bands) {
                        if (!band.frequencies) continue
                        for (let freq of band.frequencies) {
                          let ch = parseInt(freq.split(' ')[2].slice(1, -1))
                          if (ch === parseInt(channel)) {
                            primaryBand = getBand(parseInt(freq.split(' ')[0]))
                          }
                        }
                      }
                    }

                    // Second pass: find a band that isn't the primary
                    let link2Band = 0
                    for (let iw of iws) {
                      if (!iw.devices[iface]) continue
                      for (let band of iw.bands) {
                        if (!band.frequencies) continue
                        let f0 = parseInt(band.frequencies[0].split(' ')[0])
                        if (getBand(f0) !== primaryBand) {
                          link2Band = getBand(f0)
                          break
                        }
                      }
                    }

                    let bwOptions
                    if (link2Band === 1) {
                      bwOptions = ['20 MHz', '40 MHz']
                    } else if (link2Band === 2) {
                      bwOptions = ['20 MHz', '40 MHz', '80 MHz', '160 MHz']
                    } else {
                      bwOptions = ['20 MHz', '40 MHz', '80 MHz', '160 MHz', '320 MHz']
                    }
                    return bwOptions.map((bw) => (
                      <Select.Item key={bw} label={bw} value={bw} />
                    ))
                  })()}
                </Select>
              </FormControl>

              <FormControl flex={1}>
                <FormControlLabel>
                  <FormControlLabelText>Channel</FormControlLabelText>
                </FormControlLabel>
                <Select
                  selectedValue={mloChannel?.toString()}
                  onValueChange={(value) => setMloChannel(parseInt(value))}
                >
                  {(() => {
                    // Enumerate channels for Link 2 from a different band
                    let mloChannels = []
                    // Categorize frequency into band: 1=2.4GHz, 2=5GHz, 3=6GHz
                    let getBand = (f) => f < 3000 ? 1 : f < 5900 ? 2 : 3

                    // Find primary link's frequency band
                    let primaryBand = 0
                    for (let iw of iws) {
                      if (!iw.devices[iface]) continue
                      for (let band of iw.bands) {
                        if (!band.frequencies) continue
                        for (let freq of band.frequencies) {
                          let chNum = parseInt(freq.split(' ')[2].slice(1, -1))
                          if (chNum === parseInt(channel)) {
                            primaryBand = getBand(parseInt(freq.split(' ')[0]))
                          }
                        }
                      }
                    }

                    for (let iw of iws) {
                      if (!iw.devices[iface]) continue
                      for (let band of iw.bands) {
                        if (!band.frequencies) continue
                        for (let freq of band.frequencies) {
                          let frequency = parseInt(freq.split(' ')[0])
                          let chNum = parseInt(freq.split(' ')[2].slice(1, -1))

                          // Only show channels from a different band
                          if (getBand(frequency) === primaryBand) continue
                          if (freq.includes('disabled')) continue

                          // Filter by MLO bandwidth for 5/6 GHz
                          if (frequency > 5000) {
                            if (mloBandwidth == 320) {
                              if (frequency < 5900) continue
                              if (frequency % 320 != 195 && frequency % 320 != 35) continue
                            } else if (mloBandwidth == 160) {
                              if (frequency % 160 != 60 && frequency % 160 != 35) continue
                            } else if (mloBandwidth == 80) {
                              if (frequency % 80 != 60 && frequency % 80 != 35 && frequency % 80 != 65) continue
                            } else if (mloBandwidth == 40) {
                              if (frequency % 40 != 20 && frequency % 40 != 35) continue
                            }
                          }

                          mloChannels.push({
                            value: chNum.toString(),
                            label: chNum.toString(),
                          })
                        }
                      }
                    }
                    return mloChannels.map((item) => (
                      <Select.Item
                        key={item.value}
                        label={item.label}
                        value={item.value}
                      />
                    ))
                  })()}
                </Select>
              </FormControl>
            </HStack>
          </VStack>
        ) : null}

        <Button
          size="md"
          action="primary"
          sx={{
            '@base': { w: '$2/3', alignSelf: 'center' },
            '@md': { w: 120, alignSelf: 'flex-end' }
          }}
          mt="$4"
          onPress={handleSubmit}
        >
          <ButtonText>Save</ButtonText>
          <ButtonIcon as={CheckIcon} ml="$1" />
        </Button>

      </VStack>
    </>
  )
}

WifiChannelParameters.propTypes = {
  notifyChange: PropTypes.func
}

export default WifiChannelParameters
